import { apiClient } from './apiClient';
import { Units, PagedResult } from './foods';

// Bit positions must match the backend's [Flags] Allergens enum (Domain.Entities.Ingredients.Allergens),
// which starts at 1<<1, not 1<<0 - None=0 leaves bit 0 unused there.
export enum Allergens {
  None = 0,
  Gluten = 2,
  Crustaceans = 4,
  Eggs = 8,
  Fish = 16,
  Peanuts = 32,
  Soybeans = 64,
  Milk = 128,
  Nuts = 256,
  Celery = 512,
  Mustard = 1024,
  Sesame = 2048,
  Sulphites = 4096,
  Lupin = 8192,
  Molluscs = 16384
}

export interface IngredientDto {
  id: string;
  name: string;
  amount: number;
  units: Units;
  allergens: Allergens;
  created: string;
  updated?: string;
  isMeat: boolean;
}

export interface IngredientWithProductCountDto {
  ingredient: IngredientDto;
  productCount: number;
}

export interface GetIngredientsParams {
  page?: number;
  pageSize?: number;
  search?: string;
}

export interface CreateIngredientRequest {
  name: string;
  amount: number;
  units: Units;
  allergens: Allergens;
  isMeat?: boolean;
}

export interface UpdateIngredientRequest {
  id: string;
  name: string;
  amount: number;
  units: Units;
  allergens: Allergens;
  isMeat?: boolean;
}

export interface IngredientProductDto {
  id: string;
  ingredientId: string;
  productId?: string;
  productName?: string;
}

export async function getIngredients(params: GetIngredientsParams = {}): Promise<PagedResult<IngredientWithProductCountDto>> {
  const queryParams = new URLSearchParams();
  if (params.page) queryParams.set('page', params.page.toString());
  if (params.pageSize) queryParams.set('pageSize', params.pageSize.toString());
  if (params.search) queryParams.set('search', params.search);
  
  const queryString = queryParams.toString();
  const url = `/api/Ingredients${queryString ? `?${queryString}` : ''}`;
  return apiClient.get<PagedResult<IngredientWithProductCountDto>>(url);
}

export async function getAllIngredients(): Promise<IngredientDto[]> {
  // Legacy function for backward compatibility - fetches all
  const result = await getIngredients({ pageSize: 1000 });
  return result.items.map(item => item.ingredient);
}

export async function getIngredientById(id: string): Promise<IngredientDto> {
  return apiClient.get<IngredientDto>(`/api/Ingredients/${id}`);
}

export async function getIngredientsWithoutAllergens(excludedAllergens: Allergens): Promise<IngredientDto[]> {
  return apiClient.get<IngredientDto[]>(`/api/Ingredients/without-allergens?excludedAllergens=${excludedAllergens}`);
}

export async function createIngredient(request: CreateIngredientRequest): Promise<IngredientDto> {
  return apiClient.post<CreateIngredientRequest, IngredientDto>('/api/Ingredients', request);
}

export async function updateIngredient(id: string, request: UpdateIngredientRequest): Promise<void> {
  return apiClient.put<UpdateIngredientRequest, void>(`/api/Ingredients/${id}`, request);
}

export async function addProductToIngredient(ingredientId: string, productId: string): Promise<void> {
  return apiClient.post<{}, void>(`/api/Ingredients/${ingredientId}/products/${productId}`, {});
}

export async function getIngredientProducts(ingredientId: string): Promise<IngredientProductDto[]> {
  return apiClient.get<IngredientProductDto[]>(`/api/Ingredients/${ingredientId}/products`);
}

export async function removeProductFromIngredient(ingredientProductId: string): Promise<void> {
  return apiClient.delete<void>(`/api/Ingredients/ingredient-products/${ingredientProductId}`);
}

